import { forwardRef, Ref, useImperativeHandle, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ApplicationMimeTypesEnum } from "../../common/enums/ApplicationMimeTypesEnum";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { IeLynxObject } from "../../common/models/eLynxObject";
import { IAttachment } from "../../common/models/IAttachment";
import { getAttachmentUrl } from "../../common/utilities/AttachmentHelpers";
import { useConfirmation } from "../../contexts/confirmation-context/ConfirmationContext";
import { useUserContext } from "../../contexts/user-context/UserContext";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { useAttachments } from "../../queries/object-attachments/UseAttachments";
import { useDeleteAttachment } from "../../queries/object-attachments/UseDeleteAttachment";
import { useSaveAttachment } from "../../queries/object-attachments/UseSaveAttachment";
import { useObjectViewState } from "../../queries/object-view-state/UseObjectViewState";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import AttachmentsDataService from "../../services/attachments-data-service/AttachmentsDataService";
import { BaseActionEnum } from "../action-menu/ActionMenuItem";
import AttachmentDialog, { AttachmentDialogInitValues, bindAttachmentDialog, useAttachmentDialogState } from "../attachment-dialog/AttachmentDialog";
import { ContentContainer } from "../content-container/ContentContainer";
import { ContentSection } from "../content-section/ContentSection";
import { ViewOptionStateEnum } from "../contextual-app-bar/ViewOptionStateEnum";
import FileDownloader, { FileDownloaderType } from "../file-downloader/FileDownloader";
import LoadingZone from "../loading-zone/LoadingZone";
import NoData from "../no-data/NoData";
import VirtualizedList from "../virtualized-list/VirtualizedList";
import { ObjectAttachmentItem, ObjectAttachmentItemActionEnum } from "./ObjectAttachmentItem";

export type ObjectAttachmentsProps = {
    setViewOptionState?: (state: ViewOptionStateEnum) => void;
};

export type ObjectAttachmentsType = {
    executeAction: (action: BaseActionEnum | BaseActionEnum) => void;
};

function ObjectAttachments({
    setViewOptionState
}: ObjectAttachmentsProps, ref: Ref<unknown> | undefined) {
    const routeContext = useRouteContext();
    const [userState] = useUserContext();
    const { t } = useTranslation();
    const attachmentDialog = useAttachmentDialogState();
    const { confirm, acknowledge } = useConfirmation();
    const fileDownloaderRef = useRef<FileDownloaderType>(null);

    const { data: objectViewState } = useQueryNotifications(useObjectViewState)({
        customerID: userState.customer?.id as number,
        groupID: routeContext.groupID as number,
        contextObject: {
            id: routeContext.contextObject?.id as number,
            objectType: routeContext.contextObject?.objectType as eLynxObjectTypeEnum
        }
    });

    const objectName = objectViewState?.contextObject?.objectName ?? "";

    const { data, status, refetch } = useQueryNotifications(useAttachments)({
        groupID: routeContext.groupID as number,
        eLynxObject: {
            id: routeContext.contextObject?.id as number,
            objectType: routeContext.contextObject?.objectType as eLynxObjectTypeEnum
        }
    });

    const { mutate: deleteAttachment/*, status: deleteStatus*/ } = useQueryNotifications(useDeleteAttachment)({
        groupID: routeContext.groupID as number,
        eLynxObject: {
            id: routeContext.contextObject?.id as number,
            objectType: routeContext.contextObject?.objectType as eLynxObjectTypeEnum
        },
        onSuccess: (_data, variables) => {
            acknowledge(t("deleteSuccessMessage", { itemName: (t("dataTypeNames.attachment"))}));
        }
    });

    const { mutate: saveAttachment/*, status: saveStatus*/ } = useQueryNotifications(useSaveAttachment)({
        groupID: routeContext.groupID as number,
        contextObject: {
            id: routeContext.contextObject?.id as number,
            objectType: routeContext.contextObject?.objectType as eLynxObjectTypeEnum
        },
        onSuccess: (_data, variables) => {
            acknowledge(t("saveSuccessMessage", { itemName: t("dataTypeNames.attachment")}))
        }
    });

    useImperativeHandle<unknown, ObjectAttachmentsType>(
        ref,
        () => ({
            executeAction: executeAction
        })
    );

    async function onAttachmentAction(action: ObjectAttachmentItemActionEnum, item: IAttachment) {
        switch (action) {
            case ObjectAttachmentItemActionEnum.Download:
                if (item) {
                    const url = await AttachmentsDataService.getDownloadUrl(
                        routeContext.groupID as number,
                        {
                            id: routeContext.contextObject?.id as number,
                            objectType: routeContext.contextObject?.objectType as eLynxObjectTypeEnum
                        } as IeLynxObject,
                        item);

                    fileDownloaderRef.current?.download(
                        getAttachmentUrl(item.base64Data, url),
                        item.fileName
                    )
                }
                break;
            case ObjectAttachmentItemActionEnum.Delete:
                confirmAndDeleteAttachment(item);
                break;
        }
    }

    async function getDeleteAttachmentConfirmation() {
        return (await confirm({
            dialogTitle: t("deleteConfirmation.title", { itemName: t("dataTypeNames.attachment") }),
            confirmationText: t("deleteConfirmation.confirmationText"),
            yesButtonText: t("deleteConfirmation.yesButtonText"),
            noButtonText: t("deleteConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true
        })) === "yes";
    }

    async function createAttachment() {
        
        const newAttachment: AttachmentDialogInitValues = {            
            id: -1,
            deleted: false,
            mimeType: ApplicationMimeTypesEnum.Binary,
            fileName: "",
            description: ""
        };

        const result = await attachmentDialog.open(newAttachment);

        if (result.reason === "ok" && result.values) {
            newAttachment.base64Data = result.values.base64ImageData;
            newAttachment.fileName = result.values.fileName;
            newAttachment.mimeType = result.values.mimeType ?? ApplicationMimeTypesEnum.Binary;
            newAttachment.description = result.values.description;

            saveAttachment(newAttachment);
        }
    }

    async function confirmAndDeleteAttachment(attachment: IAttachment) {
        const canDelete = await getDeleteAttachmentConfirmation();

        if (canDelete) {
            deleteAttachment(attachment);
        }
    }

    function renderItem(index: number, item: IAttachment) {
        return (
            <ObjectAttachmentItem
                item={item}
                contextObject={routeContext.contextObject}
                readOnly={data?.canEdit !== true}
                onAction={onAttachmentAction}
            />
        )
    }

    function executeAction(action: BaseActionEnum | BaseActionEnum) {
        switch (action) {
            case BaseActionEnum.Refresh:
                refetch();
                break;
            case BaseActionEnum.AddAttachment:
                createAttachment();
                break;
    
            }
    }

    const noDataMessage = data?.attachments?.length === 0
        ? t('noData.noDataTypesForObject', { dataTypeName: t('dataTypeNames.attachment'), objectName })
        : undefined;

    const canEditAttachments = data?.canEdit === true;

    return (
        <>
            <LoadingZone isLoading={status === "loading"}>
                <ContentContainer>
                    <ContentSection>
                        {status === "success" && noDataMessage &&
                            <NoData text={noDataMessage} />
                        }
                        <VirtualizedList
                            items={data?.attachments as Array<IAttachment>}
                            renderItem={renderItem}
                        />
                    </ContentSection>

                </ContentContainer>
            </LoadingZone>

            <FileDownloader ref={fileDownloaderRef} />
            {canEditAttachments &&
                <AttachmentDialog {...bindAttachmentDialog(attachmentDialog)} />
            }
        </>
    )
}

export default forwardRef(ObjectAttachments);